import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, MessageSquare, UserCheck, XCircle, Loader2, Lock, Package, Globe, Mail, CheckCircle2, Copy, FileText, Banknote } from 'lucide-react';
import {
  UniversalPage,
  UniversalEmptyState,
  Card,
  Button,
  Input,
  StatusBadge,
  MoneyCell,
} from '@sighthost/ui';
import { salesService, leadPaymentBadge, packageLabel, type LeadRow, type LeadNote, type LeadOrder, type LeadQuotation } from '@/domains/sales/services/salesService';
import { introClipboardText, introMailtoHref, type IntroVariant } from '../lib/introEmail';
import { AddNoteForm } from '../components/AddNoteForm';
import { IssueQuotationModal } from '../components/IssueQuotationModal';
import { QuotationPayLinkButton } from '../components/QuotationPayLinkButton';
import { WithdrawQuotationButton } from '../components/WithdrawQuotationButton';

function shortId(id: string): string { return id.slice(0, 8); }

/**
 * SalesLeadDetailPage — record-scoped page for a single lead.
 * Includes notes thread + Convert button + Mark-lost flow.
 */

export default function SalesLeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<LeadRow | null>(null);
  const [order, setOrder] = useState<LeadOrder | null>(null);
  /** ALL live quotations, not just the latest — see LiveQuotationsCard below. */
  const [quotations, setQuotations] = useState<LeadQuotation[]>([]);
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [showLostForm, setShowLostForm] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [markingLost, setMarkingLost] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [showCaptureForm, setShowCaptureForm] = useState(false);
  const [captureRef, setCaptureRef] = useState('');
  const [capturePaidOn, setCapturePaidOn] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [captureMsg, setCaptureMsg] = useState<string | null>(null);
  const [introCopied, setIntroCopied] = useState(false);
  const [introVariant, setIntroVariant] = useState<IntroVariant>('warm');
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [{ lead, notes }, leadOrder, liveQuotes] = await Promise.all([
        salesService.getLead(id),
        salesService.getLeadOrder(id),
        salesService.listLeadQuotations(id),
      ]);
      setLead(lead);
      setOrder(leadOrder);
      setQuotations(liveQuotes);
      setNotes(notes);
      const ids = [
        ...new Set([
          ...(lead?.assigned_agent_id ? [lead.assigned_agent_id] : []),
          ...notes.map((n) => n.agent_id),
        ]),
      ];
      setAgentNames(await salesService.resolveAgentNames(ids));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onConvert() {
    if (!id) return;
    if (!confirm('Convert this lead to a customer? They will receive a magic-link claim email.')) return;
    setConverting(true);
    setActionError(null);
    try {
      const { redirect_url } = await salesService.convertLead(id);
      navigate(redirect_url);
    } catch (e) {
      setActionError((e as Error).message);
      setConverting(false);
    }
  }

  async function onResendInvoice() {
    if (!id) return;
    setResending(true);
    setActionError(null);
    setResendMsg(null);
    try {
      const { to_email } = await salesService.resendLeadInvoice(id);
      setResendMsg(`Invoice email re-sent to ${to_email}.`);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setResending(false);
    }
  }

  async function onRequestCapture(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setCapturing(true);
    setActionError(null);
    setCaptureMsg(null);
    try {
      await salesService.requestPaymentCapture(id, {
        reference: captureRef.trim() || undefined,
        paid_on: capturePaidOn.trim() || undefined,
      });
      setCaptureMsg('Payment sent to billing for verification. You’ll see the lead move to Customer once it’s confirmed.');
      setShowCaptureForm(false);
      setCaptureRef('');
      setCapturePaidOn('');
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setCapturing(false);
    }
  }

  async function onCopyIntro() {
    if (!lead) return;
    try {
      await navigator.clipboard.writeText(introClipboardText(lead, introVariant));
      setIntroCopied(true);
      setTimeout(() => setIntroCopied(false), 5000);
    } catch {
      setActionError('Could not copy — your browser blocked clipboard access. Select the text manually.');
    }
  }

  function onOpenIntro() {
    if (!lead) return;
    // Opens the agent's default mail client (Outlook) with To/Subject/body
    // pre-filled. mailto doesn't unload the SPA.
    window.location.href = introMailtoHref(lead, introVariant);
  }

  async function onMarkLost(e: FormEvent) {
    e.preventDefault();
    if (!id || lostReason.trim().length === 0) return;
    setMarkingLost(true);
    setActionError(null);
    try {
      await salesService.markLost(id, lostReason.trim());
      setShowLostForm(false);
      await reload();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setMarkingLost(false);
    }
  }

  if (loading) {
    return <UniversalPage title="Loading…"><Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card></UniversalPage>;
  }
  if (!lead) {
    return (
      <UniversalPage title="Lead not found">
        <UniversalEmptyState
          title="Lead not found"
          description="This lead may have been converted, marked lost, or you may not have access."
          action={<Button onClick={() => navigate('/sales/leads')} variant="outline"><ArrowLeft className="h-4 w-4" /> Back to leads</Button>}
        />
      </UniversalPage>
    );
  }

  const isMutable = lead.sales_stage === 'lead' || lead.sales_stage === 'quoted';
  // Option C (2026-07-09): agents may resend the invoice pay-link only while
  // the lead is still pre-conversion + unpaid. Post-payment resends route to
  // support (the EF rejects them anyway).
  const canResendInvoice =
    !!order &&
    !!order.invoice_number &&
    (order.payment_state === 'pending_payment' ||
      order.payment_state === 'unpaid' ||
      order.payment_state === 'draft');
  // Same pre-conversion + unpaid window as the resend: an agent may flag that
  // their lead paid by EFT, raising a capture request for support to verify.
  // The agent never records the payment (LD-S11 / SALES-ISO-1).
  const canRequestCapture = canResendInvoice;

  return (
    <UniversalPage title={lead.full_name} description={lead.company ?? lead.email ?? lead.phone ?? '—'}>
      <Button onClick={() => navigate('/sales/leads')} variant="ghost" className="mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to leads
      </Button>

      {lead.sales_stage === 'customer' && (
        <Card className="mb-4 border-warning/30 bg-warning/5 p-4">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 shrink-0 text-warning-dark mt-0.5" aria-hidden />
            <div className="text-sm">
              <p className="font-semibold text-ink">SightHost-managed customer — read-only</p>
              <p className="mt-1 text-muted-foreground">
                This customer has converted and is now managed by SightHost. You retain read-only visibility
                into contact details for win-back outreach and can still log notes on this record for your own
                follow-up (per LD-S11). Tickets, billing detail, cPanel access, and admin operations belong to
                SightHost. See your manager's manual §6 for the data-ownership boundary.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Detail column */}
        <div className="space-y-6 lg:col-span-2">
        {order && <SelectedPackageCard order={order} />}
        {quotations.length > 0 && (
          <LiveQuotationsCard quotations={quotations} onChanged={reload} />
        )}
        <Card className="p-6">
          <div className="mb-4 flex items-center gap-3">
            <StatusBadge tone={lead.sales_stage === 'lead' ? 'info' : lead.sales_stage === 'quoted' ? 'pending' : lead.sales_stage === 'customer' ? 'active' : 'draft'}>
              {lead.sales_stage}
            </StatusBadge>
            {lead.lead_source && <span className="text-xs text-muted-foreground">via {lead.lead_source}</span>}
          </div>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="Company" value={lead.company} />
            <Field label="Created" value={new Date(lead.created_at).toLocaleString('en-ZA')} />
            {lead.assigned_agent_id && <Field label="Assigned agent" value={agentNames[lead.assigned_agent_id] ?? shortId(lead.assigned_agent_id)} />}
            {lead.lost_reason && <Field label="Lost reason" value={lead.lost_reason} />}
          </dl>

          {/* Notes thread */}
          <section className="mt-8">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
              <MessageSquare className="h-4 w-4" /> Notes ({notes.length})
            </h2>
            <div className="mb-4">
              <AddNoteForm
                customerProfileId={id!}
                onAdded={(n) => setNotes((prev) => [n, ...prev])}
              />
            </div>
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            ) : (
              <ul className="space-y-3">
                {notes.map((n) => (
                  <li key={n.id} className="rounded-md border border-border bg-card p-3">
                    <div className="text-xs text-muted-foreground">
                      {agentNames[n.agent_id] ?? shortId(n.agent_id)} · {new Date(n.created_at).toLocaleString('en-ZA')}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{n.note_text}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </Card>
        </div>

        {/* Actions column */}
        <div className="space-y-4">
          {isMutable && (
            <Card className="p-6">
              <h3 className="mb-3 text-sm font-semibold text-ink">Actions</h3>
              {actionError && <div className="mb-3 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{actionError}</div>}
              <div className="space-y-2">
                <Button onClick={() => setQuoteModalOpen(true)} variant="primary" className="w-full">
                  <FileText className="h-4 w-4" />
                  Issue quotation
                </Button>
                <Button onClick={onConvert} variant="outline" className="w-full" disabled={converting}>
                  {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                  Convert to customer
                </Button>
                <div className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-ink">Intro email</span>
                    <div className="flex items-center gap-1 text-xs">
                      <button
                        type="button"
                        onClick={() => setIntroVariant('warm')}
                        className={introVariant === 'warm' ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-ink'}
                      >
                        Warm
                      </button>
                      <span className="text-muted-foreground">/</span>
                      <button
                        type="button"
                        onClick={() => setIntroVariant('cold')}
                        className={introVariant === 'cold' ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-ink'}
                      >
                        Cold
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={onCopyIntro} variant="outline" size="sm" className="flex-1">
                      <Copy className="h-4 w-4" /> Copy
                    </Button>
                    <Button onClick={onOpenIntro} variant="outline" size="sm" className="flex-1">
                      <Mail className="h-4 w-4" /> Outlook
                    </Button>
                  </div>
                  {introCopied && (
                    <div className="mt-2 flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-2 text-xs text-success">
                      <CheckCircle2 className="h-4 w-4 shrink-0" /> Copied — paste into a new email (add your name to the signature).
                    </div>
                  )}
                </div>
                {/* Copy-paste pay link, quotations only (owner decision
                    2026-07-27). Sits above "Resend invoice email" because
                    pasting into WhatsApp is the faster channel for most SA
                    SMEs; the email resend stays for customers who prefer it.
                    The EF is the enforcement point - it 409s a converted
                    customer - so this is gated on doc_type for UX only. */}
                {order?.doc_type === 'quotation' && (
                  <QuotationPayLinkButton
                    customerProfileId={id!}
                    customerName={lead.full_name}
                    size="default"
                    label="Get payment link"
                    fullWidth
                  />
                )}
                {canResendInvoice && (
                  <Button onClick={onResendInvoice} variant="outline" className="w-full" disabled={resending}>
                    {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    Resend invoice email
                  </Button>
                )}
                {resendMsg && (
                  <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-2 text-xs text-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0" /> {resendMsg}
                  </div>
                )}
                {canRequestCapture && (
                  !showCaptureForm ? (
                    <Button onClick={() => { setShowCaptureForm(true); setCaptureMsg(null); }} variant="outline" className="w-full">
                      <Banknote className="h-4 w-4" /> Confirm customer payment (EFT)
                    </Button>
                  ) : (
                    <form onSubmit={onRequestCapture} className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                      <p className="text-xs text-muted-foreground">
                        Flag that this customer paid by EFT. Billing will verify against the bank
                        statement and mark the invoice paid — you don’t record the payment yourself.
                      </p>
                      <Input
                        value={captureRef}
                        onChange={(e) => setCaptureRef(e.target.value)}
                        placeholder="EFT reference (optional)"
                        maxLength={120}
                        autoFocus
                      />
                      <Input
                        value={capturePaidOn}
                        onChange={(e) => setCapturePaidOn(e.target.value)}
                        placeholder="Date paid, e.g. 2026-07-23 (optional)"
                        maxLength={40}
                      />
                      <div className="flex gap-2">
                        <Button type="submit" variant="primary" className="flex-1" disabled={capturing}>
                          {capturing && <Loader2 className="h-4 w-4 animate-spin" />}
                          Send to billing
                        </Button>
                        <Button type="button" variant="outline" onClick={() => { setShowCaptureForm(false); setCaptureRef(''); setCapturePaidOn(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )
                )}
                {captureMsg && (
                  <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-2 text-xs text-success">
                    <CheckCircle2 className="h-4 w-4 shrink-0" /> {captureMsg}
                  </div>
                )}
                {!showLostForm ? (
                  <Button onClick={() => setShowLostForm(true)} variant="outline" className="w-full">
                    <XCircle className="h-4 w-4" /> Mark as lost
                  </Button>
                ) : (
                  <form onSubmit={onMarkLost} className="space-y-2">
                    <Input
                      value={lostReason}
                      onChange={(e) => setLostReason(e.target.value)}
                      placeholder="Reason (1..500 chars)"
                      maxLength={500}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button type="submit" variant="primary" className="flex-1" disabled={markingLost || lostReason.trim().length === 0}>
                        {markingLost && <Loader2 className="h-4 w-4 animate-spin" />}
                        Confirm
                      </Button>
                      <Button type="button" variant="outline" onClick={() => { setShowLostForm(false); setLostReason(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {id && (
        <IssueQuotationModal
          open={quoteModalOpen}
          onOpenChange={setQuoteModalOpen}
          customerProfileId={id}
          onIssued={reload}
          existingQuote={
            // Only a LIVE quotation can be superseded. A converted invoice is
            // money already taken — that route is a refund, not a replacement.
            order?.doc_type === 'quotation' &&
            order.payment_state === 'pending_payment' &&
            order.invoice_id
              ? {
                  invoiceId: order.invoice_id,
                  quoteNumber: order.invoice_number ?? '',
                  packageLabel: packageLabel(order),
                  amountZar: order.invoice_amount,
                  domain: order.fqdn_mode === 'later' ? null : order.domain_name,
                }
              : null
          }
        />
      )}
    </UniversalPage>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value ?? '—'}</dd>
    </div>
  );
}

/**
 * LiveQuotationsCard — every quotation this lead can still pay.
 *
 * SelectedPackageCard above it shows ONE order, because `v_sales_lead_order` is
 * a LATERAL … LIMIT 1 over the lead's latest. That is the right shape for "what
 * did they choose", and the wrong shape for "what can they still pay" — on
 * 2026-08-05 info@lionroar.group held three live quotations (R500 Studio from
 * 28 July plus two Entry quotes issued 45 minutes apart) and the agent could see
 * only the newest. Paying the Studio one would have converted it in place:
 * Studio order, R3,490 instalment schedule, R500 commission, for a product we
 * had decided not to sell.
 *
 * So this card deliberately lists ALL of them, flags the duplicate case, and
 * gives each row a Withdraw action.
 */
function LiveQuotationsCard({
  quotations,
  onChanged,
}: {
  quotations: LeadQuotation[];
  onChanged: () => void;
}) {
  const multiple = quotations.length > 1;
  const total = quotations.reduce((sum, q) => sum + Number(q.amount ?? 0), 0);

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="font-semibold text-foreground">
          Live quotations{multiple ? ` (${quotations.length})` : ''}
        </h3>
      </div>

      {multiple && (
        <div className="mb-4 rounded-md border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">
          This lead can pay <strong className="text-foreground">any</strong> of these right now —{' '}
          <MoneyCell value={total} /> in total. Withdraw the ones that no longer apply so they
          cannot pay the wrong package by mistake.
        </div>
      )}

      <ul className="space-y-2">
        {quotations.map((q) => {
          const days = q.quote_expires_at
            ? Math.floor(
                (new Date(q.quote_expires_at).getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000
              )
            : null;
          return (
            <li
              key={q.invoice_id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="tabular-nums text-xs text-muted-foreground">{q.quote_number}</span>
                  {days !== null && (
                    <StatusBadge tone={days < 0 ? 'draft' : days <= 2 ? 'failed' : days <= 7 ? 'overdue' : 'pending'}>
                      {days < 0 ? `Lapsed ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days}d left`}
                    </StatusBadge>
                  )}
                </div>
                <div className="truncate font-medium text-foreground">
                  {packageLabel(q) ?? 'Quotation'}
                  {q.order_count > 1 && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      (bundle of {q.order_count})
                    </span>
                  )}
                </div>
                {q.domain_name && q.fqdn_mode !== 'later' && (
                  <div className="truncate text-xs text-muted-foreground">{q.domain_name}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <MoneyCell value={Number(q.amount ?? 0)} />
                <WithdrawQuotationButton quotation={q} onWithdrawn={onChanged} />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function SelectedPackageCard({ order }: { order: LeadOrder }) {
  const pay = leadPaymentBadge(order.payment_state) ?? { label: 'Unknown', tone: 'draft' as const };
  const domain =
    order.fqdn_mode === 'later'
      ? 'To be decided'
      : order.domain_name ?? '—';
  const domainNote =
    order.fqdn_mode === 'register' ? 'new registration'
    : order.fqdn_mode === 'existing' ? 'existing domain'
    : 'decide later';

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Package className="h-4 w-4" aria-hidden /> Selected package
        </h2>
        <StatusBadge tone={pay.tone}>{pay.label}</StatusBadge>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">Hosting package</dt>
          <dd className="mt-0.5 text-base font-semibold text-ink">{packageLabel(order) ?? '—'}</dd>
          {order.invoice_amount != null && (
            <dd className="mt-0.5 text-sm text-muted-foreground">
              <MoneyCell value={order.invoice_amount} /> ·{' '}
              {order.doc_type === 'quotation' ? (
                <>
                  Quotation {order.invoice_number ?? '—'}
                  {order.quote_expires_at && (
                    <> (expires {new Date(order.quote_expires_at).toLocaleDateString('en-ZA')})</>
                  )}
                </>
              ) : (
                <>{order.invoice_number ?? '—'}</>
              )}
            </dd>
          )}
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">Domain</dt>
          <dd className="mt-0.5 flex items-center gap-1.5 text-base font-semibold text-ink">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> {domain}
          </dd>
          <dd className="mt-0.5 text-sm text-muted-foreground">{domainNote}</dd>
        </div>
      </div>
    </Card>
  );
}
